import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import Notifications from '../../../../services/notifications';
import Loader from '../../../../services/loader';
import { handleValidationFrequencies } from '../helpers/orderHelper';
import Modal from '../../../../commonComponents/modal/Modal';
import ModalHeader from '../../../../commonComponents/modal/ModalHeader';
import FormInput from '../../../../commonComponents/forms/FormInput';
import TextFilter from '../../../../commonComponents/lists/filters/TextFilter';
import ProductItem from './components/ProductItem';
import ModalFooter from '../../../../commonComponents/modal/ModalFooter';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import AddressProp from '../../../../commonComponents/propTypes/addressProp';
import CategoryProp from '../../../shop/propTypes/categoryProp';
import translate from '../../../../commonComponents/translate';
import { compose } from '../../../../services/utilityFunctions';
import ScrollContainer from '../../../../commonComponents/container/ScrollContainer';

function AddNewProductModal(props) {
  const {
    t,
    onClose,
    payer,
    receiver,
    selectedShippingAddress,
    categories,
    catalogTypes,
    productVariations,
    endpoint,
  } = props;

  // Loader ref
  const loader = useRef(new Loader('#product-list'));

  // State so we know if it's needed to get more items
  const [metaInfoTotal, setMetaInfoTotal] = useState(null);
  const [page, setPage] = useState(1);

  // All products to display to the user
  const [allVariations, setAllVariations] = useState([]);
  // Filters state
  const [filters, setFilters] = useState({});

  // Products that will be added to the list
  const [productsToAdd, setProductsToAdd] = useState(productVariations || []);

  // region Handles popup exit
  /**
   * Handles the "exit" of the popup, can be with or without products
   * @products {MarketProductVariations[]} - variations to add
   */
  const handleClose = products => {
    if (!products) {
      onClose(null);
    } else {
      const validatedProducts = handleValidationFrequencies(
        productsToAdd,
        productVariations,
      );
      if (validatedProducts.length !== products.length) {
        onClose(validatedProducts, productVariations);
      } else {
        onClose(validatedProducts);
      }
    }
  };
  // endregion

  // region Handles the checkbox click
  /**
   * Handles the event on the checkbox
   * If variation already exist there, we remove it
   * if not we add it
   * @variationId {string} _id of variation
   */
  const addProduct = useCallback(
    variation => {
      setProductsToAdd(prevState => {
        const isInAddAlready = prevState?.find(v => v._id === variation._id);
        if (isInAddAlready) {
          return prevState.filter(v => v._id !== variation._id);
        }
        let frequency = 'only_once';
        if (
          variation?.parentProduct?.availableForSubscription &&
          !variation?.parentProduct?.availableSubscriptionFrequency?.includes(
            'only_once',
          )
        ) {
          frequency =
            variation?.parentProduct?.availableSubscriptionFrequency[0];
        }
        return [...prevState, { ...variation, frequency }];
      });
    },
    [setProductsToAdd],
  );
  // endregion Handles the checkbox click

  // region handles filters change
  const handleFiltersChange = useCallback(
    field => value => {
      setFilters(prevState => {
        if (field === 'productType') {
          return {
            ...prevState,
            [field]: value,
            category: null,
          };
        }
        return {
          ...prevState,
          [field]: value,
        };
      });
    },
    [setFilters],
  );
  // endregion handles filters change

  // region Handles Search Change with debounce
  /**
   * Handles search input with timeout
   * @value {string}
   */
  // Create a ref to store the timeout ID
  const debounceTimeoutRef = useRef(null);
  const handleSearchChange = useCallback(
    value => {
      // Clear the previous timeout if it exists
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set a new timeout with a delay of 400ms
      debounceTimeoutRef.current = setTimeout(() => {
        setFilters(prevState => ({
          ...prevState,
          search: value,
        }));
      }, 400);
    },
    [setFilters],
  );
  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    },
    [],
  );
  // endregion Handles Search Change with debounce

  // region INPUT PROPS
  const inputProps = useCallback(
    field => {
      const categoriesItems = filters?.productType
        ? categories.filter(category =>
            category.catalogTypes.includes(filters.productType),
          )
        : categories;
      switch (field) {
        case 'productType':
          return {
            allowClear: true,
            dropdownParent: '#addNewProductModal',
            items: catalogTypes.map(catalog => ({
              id: catalog.catalogType,
              text: catalog.name,
            })),
          };
        case 'categories':
          return {
            allowClear: true,
            dropdownParent: '#addNewProductModal',
            items: categoriesItems.map(category => ({
              id: category._id,
              text: category.name,
            })),
          };
        default:
          return {};
      }
    },
    [categories, catalogTypes, filters.productType],
  );
  // endregion INPUT PROPS

  // region HANDLE SCROLLABLE PRODUCTS
  /**
   * Request to API to Get ITEMS
   * @type {(function(number=, {}=): Promise<*|[]|undefined>)|*}
   */
  const fetchItems = async (
    filtersParam = {},
    pageNumber = 1,
    initVariations = [],
  ) => {
    try {
      loader.current.show();
      const url =
        endpoint ||
        '/table/market/product/available/variation/list?populate=form';

      // region MANAGE FILTERS
      // by default the receiver is the payer
      const localReceiver = receiver || payer;

      let country;
      // Check if a shipping address was chosen, if not use billing address
      if (selectedShippingAddress?.countryCode) {
        country = selectedShippingAddress.countryCode;
      } else if (localReceiver?.address?.countryCode) {
        country = localReceiver.address.countryCode;
      } else {
        // If no shipping address or billing address, use shipping country
        country = localReceiver?.shippingCountry;
      }

      const params = {
        isPublic: true,
        country,
        currency: payer?.currency || 'eur',
        user: payer?._id,
        ...filtersParam,
      };
      if (params.search === '') {
        delete params.search;
      }

      if (productVariations) {
        const variations = productVariations?.filter(
          product => !product.isPack,
        );
        if (variations?.length > 0) {
          params.excludedVariations = productVariations?.map(p => p._id);
        }
        const packs = productVariations?.filter(product => product.isPack);
        if (packs?.length > 0) {
          params.excludedProducts = packs?.map(p => p._id);
        }
      }
      // endregion MANAGE FILTERS

      const { data: response } = await axios.get(url, {
        params: {
          pagination: { perpage: 10, page: pageNumber },
          ...params,
        },
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Games',
        },
      });
      const { data, meta } = response;
      return {
        pageNumber,
        newProducts: [...initVariations, ...data],
        pages: meta?.pages,
      };
    } catch (err) {
      Notifications.showErrorFromRequest(err, 'error_fetchProducts');
      return {
        pageNumber: 1,
        newProducts: [],
        pages: 1,
      };
    } finally {
      loader.current.hide();
    }
  };

  /** region Gets first batch of items for page 1 */
  useEffect(() => {
    (async () => {
      const { newProducts, pages } = await fetchItems(filters, 1, []);
      setMetaInfoTotal(pages);
      setAllVariations(newProducts);
      setPage(1);
    })();
  }, [filters]);

  /**
   * When scroll container gets to the end, we get another batch of items
   * If page is not greater that total of pages
   * And then we update the allVariation state
   * @type {(function(): Promise<void>)|*}
   */
  const isFetchingRef = useRef(false);
  const handleReachedEnd = useCallback(async () => {
    if (isFetchingRef.current) return; // Exit if a fetch is in progress

    isFetchingRef.current = true; // Set fetching status to true

    if (!metaInfoTotal || page + 1 <= metaInfoTotal) {
      const { pageNumber, newProducts, pages } = await fetchItems(
        filters,
        page + 1,
        allVariations,
      );
      setPage(pageNumber);
      setMetaInfoTotal(pages);
      setAllVariations(newProducts);
    }

    isFetchingRef.current = false; // Reset fetching status after fetch is done
  }, [
    setAllVariations,
    setPage,
    setMetaInfoTotal,
    filters,
    metaInfoTotal,
    page,
    allVariations,
  ]);
  // endregion HANDLE SCROLLABLE PRODUCTS

  return (
    <Modal id="addNewProductModal">
      <div
        className="modal-dialog modal-dialog-centered modal-lg"
        role="document"
      >
        <div className="modal-content">
          <ModalHeader title={t('order_add_new_product')} />
          <div className="modal-body">
            <div className="products-filters-wrapper">
              <div className="top">
                <FormInput
                  id="catalogType"
                  name="catalogType"
                  value={filters?.productType}
                  onChange={handleFiltersChange('productType')}
                  type="select"
                  placeholder={t('product-catalogType')}
                  inputProps={inputProps('productType')}
                />
                <FormInput
                  id="category"
                  name="category"
                  value={filters?.category}
                  onChange={handleFiltersChange('category')}
                  type="select"
                  placeholder={t('product.category')}
                  inputProps={inputProps('categories')}
                />
              </div>
              <TextFilter
                id="searchFilter"
                onChange={handleSearchChange}
                placeholder={t('filters.search.products')}
                value={filters?.search}
                isSearch
              />
            </div>
            <div id="product-list">
              <div className="content-wrapper">
                {allVariations?.length > 0 ? (
                  <ScrollContainer
                    onReachedEnd={handleReachedEnd}
                    onReachedEndDelay={1500}
                    containerClass="product-list-wrapper"
                  >
                    {allVariations?.map((variation, index) => (
                      <ProductItem
                        key={`${variation._id}_${index}`}
                        variation={variation}
                        onClick={addProduct}
                      />
                    ))}
                  </ScrollContainer>
                ) : (
                  <span className="hint-form">{t('noProducts')}</span>
                )}
              </div>
            </div>
          </div>
          <ModalFooter
            onSave={() => handleClose(productsToAdd)}
            cancelText={t('cancel')}
            onCancel={() => handleClose(null)}
            saveText={t('add.product')}
            disabledSave={productsToAdd?.length === 0}
          />
        </div>
      </div>
    </Modal>
  );
}

AddNewProductModal.propTypes = {
  t: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  payer: PropTypes.shape(UserProp.propType).isRequired,
  receiver: PropTypes.shape(UserProp.propType),
  selectedShippingAddress: PropTypes.shape(AddressProp.propType),
  categories: PropTypes.arrayOf(PropTypes.shape(CategoryProp.propType))
    .isRequired,
  catalogTypes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      type: PropTypes.string,
      isActive: PropTypes.bool,
      translations: PropTypes.shape({}),
    }),
  ).isRequired,
  productVariations: PropTypes.arrayOf(PropTypes.string),
  endpoint: PropTypes.string,
};

AddNewProductModal.defaultProps = {
  productVariations: [],
  endpoint: null,
  receiver: null,
  selectedShippingAddress: null,
};

export default compose(translate)(AddNewProductModal);
